import type { TFunction } from 'i18next'
import type { Activity, Project, User } from '@witty-board/shared'
import { formatDueDate } from './due-date.js'

export interface ActivityContext {
  t: TFunction
  users: Map<string, User>
  projects: Map<string, Project>
  locale: string
}

function userName(id: string | null, ctx: ActivityContext): string {
  if (!id) return ctx.t('common.unassigned')
  return ctx.users.get(id)?.name ?? id
}

function projectName(id: string | null, ctx: ActivityContext): string {
  if (!id) return '—'
  return ctx.projects.get(id)?.name ?? id
}

function statusLabel(value: string | null, ctx: ActivityContext): string {
  return value ? ctx.t(`board.columns.${value}`) : '—'
}

function priorityLabel(value: string | null, ctx: ActivityContext): string {
  return value ? ctx.t(`task.priority.${value}`) : '—'
}

function sizeLabel(value: string | null, ctx: ActivityContext): string {
  return value ?? ctx.t('common.noSize')
}

function dueDateLabel(value: string | null, ctx: ActivityContext): string {
  return value ? formatDueDate(value, ctx.locale) : ctx.t('common.noDueDate')
}

function describeFieldChange(entry: Activity, ctx: ActivityContext): string {
  switch (entry.field) {
    case 'title':
      return ctx.t('activity.titleChanged', { from: entry.oldValue, to: entry.newValue })
    case 'status':
      return ctx.t('activity.statusChanged', {
        from: statusLabel(entry.oldValue, ctx),
        to: statusLabel(entry.newValue, ctx),
      })
    case 'assigneeId':
      return ctx.t('activity.assigneeChanged', {
        from: userName(entry.oldValue, ctx),
        to: userName(entry.newValue, ctx),
      })
    case 'size':
      return ctx.t('activity.sizeChanged', {
        from: sizeLabel(entry.oldValue, ctx),
        to: sizeLabel(entry.newValue, ctx),
      })
    case 'priority':
      return ctx.t('activity.priorityChanged', {
        from: priorityLabel(entry.oldValue, ctx),
        to: priorityLabel(entry.newValue, ctx),
      })
    case 'dueDate':
      return ctx.t('activity.dueDateChanged', {
        from: dueDateLabel(entry.oldValue, ctx),
        to: dueDateLabel(entry.newValue, ctx),
      })
    case 'projectId':
      return ctx.t('activity.projectChanged', {
        from: projectName(entry.oldValue, ctx),
        to: projectName(entry.newValue, ctx),
      })
    case 'description':
      return ctx.t('activity.descriptionChanged')
    case 'parentId':
      return ctx.t('activity.orphaned')
    case null:
    default:
      return ctx.t('activity.genericUpdate')
  }
}

/**
 * Frase corta para la actividad reciente del Inicio (fase 7). Va seguida de la
 * clave y el título de la tarea, así que es un verbo y poco más; solo cubre lo
 * que devuelve `highlights`, el resto cae en un genérico.
 */
export function describeFeedEntry(entry: Activity, ctx: ActivityContext): string {
  switch (entry.type) {
    case 'created':
      return ctx.t('home.activity.created')
    case 'commented':
      return ctx.t('home.activity.commented')
    case 'deleted':
      return ctx.t('home.activity.deleted')
    case 'restored':
      return ctx.t('home.activity.restored')
    case 'updated':
      if (entry.field === 'status') {
        return entry.newValue === 'done'
          ? ctx.t('home.activity.completed')
          : ctx.t('home.activity.statusChanged', { status: statusLabel(entry.newValue, ctx) })
      }
      if (entry.field === 'assigneeId') {
        return entry.newValue
          ? ctx.t('home.activity.assigned', { name: userName(entry.newValue, ctx) })
          : ctx.t('home.activity.unassigned')
      }
      return ctx.t('home.activity.updated')
  }
}

/** Traduce una fila de `activity` a una frase legible para el hilo unificado. */
export function describeActivity(entry: Activity, ctx: ActivityContext): string {
  switch (entry.type) {
    case 'created':
      return ctx.t('activity.created')
    case 'deleted':
      return ctx.t('activity.deleted')
    case 'restored':
      return ctx.t('activity.restored')
    case 'commented':
      /** No se pinta como línea: el propio comentario ya aparece en el hilo (ver docs/DECISIONS.md, fase 4). */
      return ''
    case 'updated':
      return describeFieldChange(entry, ctx)
  }
}
